// One contact per person. Pure helpers (no database calls) shared by the app and, through a generated
// CommonJS copy (functions/mcp/dedupe.js), by the AI connector.
// A match on phone or email is "strong" (same person). A match on name only is "weak" (shown as a
// suggestion on the admin Duplicates page, never linked automatically).

const str = (v) => (v == null ? '' : String(v).trim());
export const normPhone = (v) => { const d = str(v).replace(/\D/g, '').replace(/^1(?=\d{10}$)/, ''); return d.length === 10 ? d : ''; };
export const normEmail = (v) => { const e = str(v).toLowerCase(); return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? e : ''; };
export const normName = (c) => {
  const n = [str(c && c.firstName), str(c && c.lastName)].filter(Boolean).join(' ') || str(c && c.name);
  return n.toLowerCase().replace(/[^a-z ]/g, '').replace(/\s+/g, ' ').trim();
};
// Every phone the contact has, including numbers removed for Do Not Call (so a re-added number still matches).
export const phonesOf = (c) => {
  if (!c) return [];
  const removed = (c.optOut && c.optOut.dnc && c.optOut.dnc.removedPhones) || [];
  return [...new Set([c.phone, c.phone2, ...(Array.isArray(c.additionalPhones) ? c.additionalPhones : []), ...removed].map(normPhone).filter(Boolean))];
};
export const emailsOf = (c) => (c ? [...new Set([c.email, c.email2].map(normEmail).filter(Boolean))] : []);

// Why two records look like the same person: subset of ['phone', 'email', 'name'].
export const matchReasons = (a, b) => {
  const r = [];
  const pa = phonesOf(a); if (pa.length && phonesOf(b).some((p) => pa.includes(p))) r.push('phone');
  const ea = emailsOf(a); if (ea.length && emailsOf(b).some((e) => ea.includes(e))) r.push('email');
  const na = normName(a); if (na && na.includes(' ') && na === normName(b)) r.push('name');
  return r;
};
export const isStrong = (reasons) => reasons.includes('phone') || reasons.includes('email');

const asEntries = (contacts) => (Array.isArray(contacts) ? contacts.map((c) => [c.id, c]) : Object.entries(contacts || {}));

// Existing contacts that look like `data`. Merged-away records are ignored. Strong matches first.
export const findDuplicateContacts = (data, contacts, excludeId = null) => asEntries(contacts)
  .filter(([id, c]) => c && id !== excludeId && !c.mergedInto)
  .map(([id, c]) => ({ id, contact: c, reasons: matchReasons(data, c) }))
  .filter((m) => m.reasons.length)
  .map((m) => ({ ...m, strong: isStrong(m.reasons) }))
  .sort((a, b) => Number(b.strong) - Number(a.strong) || b.reasons.length - a.reasons.length);

// The one existing contact to reuse automatically (phone or email match only), or null.
export const findSameContactId = (data, contacts, excludeId = null) => {
  const m = findDuplicateContacts(data, contacts, excludeId).find((x) => x.strong);
  return m ? m.id : null;
};

// Groups of contacts that look like the same person (for the admin Duplicates page). Suggestions only.
export const duplicateGroups = (contacts) => {
  const list = asEntries(contacts).filter(([, c]) => c && !c.mergedInto);
  const parent = new Map(list.map(([id]) => [id, id]));
  const find = (x) => { while (parent.get(x) !== x) { parent.set(x, parent.get(parent.get(x))); x = parent.get(x); } return x; };
  const reasons = new Map();
  const index = new Map();
  list.forEach(([id, c]) => {
    const keys = [...phonesOf(c).map((p) => `phone:${p}`), ...emailsOf(c).map((e) => `email:${e}`), ...(normName(c).includes(' ') ? [`name:${normName(c)}`] : [])];
    keys.forEach((k) => {
      if (index.has(k)) { const other = index.get(k); const a = find(id); const b = find(other); if (a !== b) parent.set(a, b); const root = find(id); reasons.set(root, new Set([...(reasons.get(root) || []), ...(reasons.get(a) || []), ...(reasons.get(b) || []), k.split(':')[0]])); }
      else index.set(k, id);
    });
  });
  const groups = new Map();
  list.forEach(([id]) => { const r = find(id); if (!groups.has(r)) groups.set(r, []); groups.get(r).push(id); });
  return [...groups.entries()].filter(([, ids]) => ids.length > 1).map(([root, ids]) => {
    const why = new Set(); ids.forEach((a) => ids.forEach((b) => { if (a < b) matchReasons(Object.fromEntries(list)[a], Object.fromEntries(list)[b]).forEach((x) => why.add(x)); }));
    return { ids, reasons: [...why] };
  }).sort((a, b) => Number(isStrong(b.reasons)) - Number(isStrong(a.reasons)));
};

// What the kept contact gets from the one being merged in: fills empty fields, never overwrites, unions
// lists and link maps, appends notes. Opt-outs and Do Not Call carry over (the stricter setting wins).
const MAP_KEYS = ['sellerInterestIds', 'buyerInterestIds', 'transactionIds', 'linkedContactIds', 'sharedWith'];
const LIST_KEYS = ['contactTypes', 'tags'];
const STRICT_KEYS = ['dnc', 'optOutCalls', 'optOutTexts', 'email_opt_out'];
const SKIP = new Set(['id', 'createdAt', 'createdBy', 'updatedAt', 'notes', 'optOut', 'mergedInto', 'mergedFrom', ...MAP_KEYS, ...LIST_KEYS, ...STRICT_KEYS]);
export const mergePatch = (keep, other) => {
  const p = {};
  Object.entries(other || {}).forEach(([k, v]) => { if (SKIP.has(k) || v == null || v === '') return; if (keep[k] == null || keep[k] === '' || (Array.isArray(keep[k]) && !keep[k].length)) p[k] = v; });
  MAP_KEYS.forEach((k) => { if (other[k] && typeof other[k] === 'object') p[k] = { ...(other[k] || {}), ...(keep[k] || {}) }; });
  LIST_KEYS.forEach((k) => { const u = [...new Set([...(keep[k] || []), ...(other[k] || [])])]; if (u.length !== (keep[k] || []).length) p[k] = u; });
  STRICT_KEYS.forEach((k) => { if (other[k] && !keep[k]) p[k] = true; });
  if (other.optOut) p.optOut = { ...other.optOut, ...(keep.optOut || {}) };
  const removed = [...new Set([...(((keep.optOut || {}).dnc || {}).removedPhones || []), ...(((other.optOut || {}).dnc || {}).removedPhones || [])])];
  if (removed.length) p.optOut = { ...(p.optOut || keep.optOut || {}), dnc: { ...(((p.optOut || keep.optOut || {}).dnc) || {}), removedPhones: removed } };
  // A second phone the kept record doesn't have is saved as phone2, not lost.
  const kp = normPhone(keep.phone); const op = normPhone(other.phone);
  if (op && kp && op !== kp && !phonesOf(keep).includes(op)) { if (!keep.phone2) p.phone2 = op; else p.additionalPhones = [...new Set([...(keep.additionalPhones || []), op])]; }
  const oe = normEmail(other.email); if (oe && normEmail(keep.email) && oe !== normEmail(keep.email) && !keep.email2) p.email2 = oe;
  if (str(other.notes) && !str(keep.notes).includes(str(other.notes))) p.notes = [str(keep.notes), str(other.notes)].filter(Boolean).join('\n\n');
  if (p.dnc) { delete p.phone; delete p.phone2; delete p.additionalPhones; }
  delete p[other.mergedFrom ? '' : 'mergedFrom'];
  p.mergedFrom = { ...(keep.mergedFrom || {}), [other.id || 'unknown']: true };
  return p;
};
